/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sync audiences", description = "Sync audiences to downstream destinations using Hightouch's sync templates.", lastUpdatedDate = new Date(1741564800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sync audiences",
        description: "Sync audiences to downstream destinations using Hightouch's sync templates.",
        lastUpdatedDate: "Mon Mar 10 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Required skillset",
          "depth": 1,
          "id": "required-skillset"
        }, {
          "value": "Add a sync",
          "depth": 0,
          "id": "add-a-sync"
        }, {
          "value": "View match rates",
          "depth": 0,
          "id": "view-match-rates"
        }, {
          "value": "Supported destinations",
          "depth": 1,
          "id": "supported-destinations"
        }, {
          "value": "Sync templates",
          "depth": 0,
          "id": "sync-templates"
        }, {
          "value": "Create a sync template",
          "depth": 1,
          "id": "create-a-sync-template"
        }, {
          "value": "Create syncs from a template",
          "depth": 1,
          "id": "create-syncs-from-a-template"
        }, {
          "value": "Unlock sync template fields",
          "depth": 1,
          "id": "unlock-sync-template-fields"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      img: "img",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      em: "em",
      ol: "ol"
    }, _provideComponents(), props.components), {Heading} = _components;
    if (!Heading) _missingMdxReference("Heading", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio is available as an add-on to ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-diagram-sync.png",
          alt: "Audience sync diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "created an audience"
        }), ", the last step is to sync it to your desired destination(s)."]
      }), "\n", _jsx(_components.p, {
        children: "For example, say you've created an audience of users who've added a particular product type to their cart without purchasing it. You likely want to sync this audience to ad platforms like Facebook or Google so you can target them with personalized ads about the product in their cart."
      }), "\n", _jsxs(_components.p, {
        children: ["Sync configuration for an audience is the same as any other sync configuration. You need to ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "map fields"
        }), ", ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "schedule"
        }), " the sync, and ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "set up alerts"
        }), " as desired."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're syncing multiple audiences, ", _jsx(_components.a, {
          href: "#sync-templates",
          children: _jsx(_components.strong, {
            children: "sync templates"
          })
        }), " can help you streamline your audience sync setup."]
      }), "\n", _jsx(_components.h3, {
        id: "required-skillset",
        children: "Required skillset"
      }), "\n", _jsx(_components.p, {
        children: "Sync configuration requires an understanding of your data and business goals. Business stakeholders can usually complete this step, but you may need to loop in your technical team depending on your implementation."
      }), "\n", _jsx(Tip, {
        children: _jsx(Heading, {
          as: "p",
          children: _jsx(_components.p, {
            children: "Share this page with your technical team if you need assistance with sync\nconfiguration."
          })
        })
      }), "\n", _jsx(_components.h2, {
        id: "add-a-sync",
        children: "Add a sync"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a sync for an existing audience, click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/add-sync.png",
          alt: "Adding a sync to an audience"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can then select existing ", _jsx(_components.a, {
          href: "#sync-templates",
          children: "sync templates"
        }), " or choose to ", _jsx(_components.strong, {
          children: "Create a new sync"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/create-new-sync.png",
          alt: "Creating a new sync for an audience"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on creating syncs, check out the ", _jsx(_components.a, {
          href: "/syncs/overview",
          children: "sync configuration"
        }), " docs or refer to destination docs for specific configuration details."]
      }), "\n", _jsxs(_components.p, {
        children: ["Before creating a new sync, it's worth considering whether ", _jsx(_components.a, {
          href: "#sync-templates",
          children: "sync templates"
        }), " are a good fit for your use case."]
      }), "\n", _jsx(_components.h2, {
        id: "view-match-rates",
        children: "View match rates"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch surfaces ", _jsx(_components.strong, {
          children: "match rates"
        }), " for syncs to paid advertising destinations. Match rates show the number of users (by both raw count and percentage) that the downstream ad platform recognized."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can view match rates for all of an audience's paid ad syncs on the audience's ", _jsx(_components.strong, {
          children: "Syncs"
        }), " tab. An individual sync's match rate also appears on the ", _jsx(_components.a, {
          href: "/syncs/overview#individual-sync-overview-page",
          children: "sync's overview page"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/match-rates.png",
          alt: "Audience match rates in Hightouch"
        })
      }), "\n", _jsx(_components.p, {
        children: "Clicking the match rate opens a details view that shows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "external status of the sync"
        }), "\n", _jsx(_components.li, {
          children: "matched users"
        }), "\n", _jsx(_components.li, {
          children: "match percentage"
        }), "\n", _jsx(_components.li, {
          children: "links to your audiences in the ad platform"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're interested in increasing your match rates, check out Hightouch's ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "supported-destinations",
        children: "Supported destinations"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch calculates and displays match rates for syncs to the following destinations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook",
            children: "Facebook Custom Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "Snapchat"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/linkedin",
            children: "LinkedIn Ads"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-templates",
        children: "Sync templates"
      }), "\n", _jsxs(_components.p, {
        children: ["When managing many audiences, it can become tedious to recreate the same sync configuration each time. For example, you may want to sync several ", _jsx(_components.a, {
          href: "https://hightouch.com/blog/driving-paid-media-roas-through-audience-activation#lookalike-audiences",
          children: "Lookalike audiences"
        }), " to Facebook. Hightouch offers a tool called ", _jsx(_components.strong, {
          children: "sync templates"
        }), " to streamline your audience sync setup."]
      }), "\n", _jsxs(_components.p, {
        children: ["Sync templates let you define sync configuration settings ", _jsx(_components.em, {
          children: "once"
        }), " for a particular destination. You create the sync template and then use it to add syncs for each audience requiring the same configuration settings and schedule. You can also use multiple templates for an audience if you're sending it to several destinations."]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync-template",
        children: "Create a sync template"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Templates"
          }), " in the navigation bar, and select the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/schema-v2/settings/sync-templates",
            children: _jsx(_components.strong, {
              children: "Sync Templates"
            })
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add sync template"
          }), " to launch the template creation flow."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/create-sync-template.png",
          alt: "Sync templates setup"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select an existing ", _jsx(_components.strong, {
            children: "Parent Model"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Configure your sync as you would typically: creating field mappings and selecting a schedule. Refer to destination docs for specific configuration details."
        }), "\n", _jsx(_components.li, {
          children: "Once finished, give the template a name."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can now use the sync template for multiple audience syncs."
      }), "\n", _jsx(_components.h3, {
        id: "create-syncs-from-a-template",
        children: "Create syncs from a template"
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You can only use sync templates for audiences derived from the same parent\nmodel as the sync template."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/audiences",
            children: [_jsx(_components.strong, {
              children: "Audiences"
            }), " page"]
          }), ", select an audience that you'd like to sync using a sync template."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add sync"
          }), " from the audience page, and select one or more templates."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/add-syncs.png",
          alt: "Add syncs"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add syncs"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Your audience now syncs according to the sync template configuration, including the template's schedule. Syncs created from templates inherit changes made to the parent sync template."
      }), "\n", _jsx(_components.h3, {
        id: "unlock-sync-template-fields",
        children: "Unlock sync template fields"
      }), "\n", _jsx(_components.p, {
        children: "Templates can be configured to leave fields \"unlocked\" so that underlying syncs can supply\ntheir own values."
      }), "\n", _jsx(_components.p, {
        children: "This feature is currently supported for syncs to the following destinations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/braze",
            children: "Braze"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook",
            children: "Facebook Custom Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/iterable",
            children: "Iterable"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/sfmc",
            children: "Salesforce Marketing Cloud"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/snapchat",
            children: "Snapchat"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Please reach out to our Customer Success team to enable this feature for your workspace."
        })
      }), "\n", _jsx(_components.p, {
        children: "To unlock a field, go to the template configuration and toggle the switch labeled \"Lock this field\". By default, all fields are locked."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/lock-field.png",
          alt: "Lock this field"
        })
      }), "\n", _jsx(_components.p, {
        children: "Some fields are dependent on each other. In such cases, you may need to unlock a dependent field first.\nHovering over the switch will inform you of any dependencies."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/field-dependency.png",
          alt: "Dependent field"
        })
      }), "\n", _jsx(_components.p, {
        children: "After declaring which fields are unlocked, attach the template to an audience as described above."
      }), "\n", _jsx(_components.p, {
        children: "If the attached template has unlocked fields, you will have the option to assign a sync-level value which will override the template's value.\nValues that differ from the sync template's default are indicated by the \"Custom value\" label."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/assign-overrides.png",
          alt: "Assign overrides"
        })
      }), "\n", _jsx(_components.p, {
        children: "These overrides can be updated at any time in the future by going to the sync configuration."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
